import { useEffect, useState, useCallback } from "react";
import { Plus, ArrowLeft, Mail, LayoutGrid, FolderKanban, Users, X, Trash2 } from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import ProjectPanel, { PROJECT_STATUS, DEVIS_STATUS } from "./ProjectPanel";
import { SECTEURS } from "../../data/projects";

const emptyContact = () => ({ email: "", phone: "" });

// Gestion des accès clients d'un projet existant (ajout / suppression d'emails)
function ClientAccess({ project }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase
      .from("project_clients")
      .select("id,email,phone")
      .eq("project_id", project.id)
      .order("created_at");
    setRows(data || []);
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    const mail = email.trim().toLowerCase();
    const tel = phone.trim();
    if (!mail && !tel) return; // au moins un des deux
    setBusy(true);
    await supabase.from("project_clients").insert({ project_id: project.id, email: mail || null, phone: tel || null });
    setEmail(""); setPhone(""); setBusy(false);
    load();
  };
  const remove = async (id) => {
    await supabase.from("project_clients").delete().eq("id", id);
    load();
  };

  return (
    <div className="bento p-6 mb-6">
      <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">Accès clients ({rows.length})</h3>
      <div className="space-y-2 mb-4">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] px-4 py-2.5">
            <div className="min-w-0">
              <div className="text-sm text-zinc-200 truncate">{r.email || r.phone}</div>
              {r.email && r.phone && <div className="text-[11px] text-zinc-500">{r.phone}</div>}
              {!r.email && <div className="text-[10px] uppercase tracking-wider text-zinc-600">Téléphone seul — pas d'accès en ligne</div>}
            </div>
            <button onClick={() => remove(r.id)} className="shrink-0 text-zinc-500 hover:text-red-400" title="Retirer l'accès">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-zinc-500">Aucun accès client. Ce projet n'est visible que par vous (et les développeurs).</p>}
      </div>
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
        <input className="field flex-1" type="email" placeholder="Email du client" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="field sm:w-40" type="tel" placeholder="Téléphone (opt.)" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <button className="btn-pill btn-pill-accent justify-center shrink-0" disabled={busy}>{busy ? "…" : "Ajouter"} <Plus className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

const secteurOptions = SECTEURS.filter((s) => s.id !== "tous");
const DRAFT_KEY = "ml_new_project_draft";
const EN_COURS = ["brief", "design", "developpement", "livraison"];
const LEAD_STATUS = [
  { id: "nouveau", label: "Nouveau" },
  { id: "contacte", label: "Contacté" },
  { id: "rdv", label: "RDV" },
  { id: "signe", label: "Signé" },
  { id: "perdu", label: "Perdu" },
];

export default function AdminDashboard({ profile }) {
  const [tab, setTab] = useState("apercu");
  const [projects, setProjects] = useState([]);
  const [stagesByProject, setStagesByProject] = useState({});
  const [selected, setSelected] = useState(null);
  const [leads, setLeads] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY));
      if (saved && typeof saved === "object") {
        // Compat ancien brouillon { client_email } -> nouveau { clients: [] }
        const clients = Array.isArray(saved.clients)
          ? saved.clients
          : (saved.client_email ? [{ email: saved.client_email, phone: "" }] : [emptyContact()]);
        return { name: saved.name || "", secteur: saved.secteur || "", clients: clients.length ? clients : [emptyContact()] };
      }
    } catch {}
    return { name: "", secteur: "", clients: [emptyContact()] };
  });

  useEffect(() => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); } catch {} }, [form]);

  const loadProjects = async () => {
    const { data } = await supabase.from("projects").select("*").order("created_at", { ascending: false });
    setProjects(data || []);
    const { data: st } = await supabase.from("stages").select("project_id,label,position,status").order("position");
    const map = {};
    (st || []).forEach((s) => {
      const m = (map[s.project_id] = map[s.project_id] || { total: 0, done: 0, current: null });
      m.total++;
      if (s.status === "termine") m.done++;
      else if (!m.current) m.current = s.label;
    });
    Object.values(map).forEach((m) => { m.pct = m.total ? Math.round((m.done / m.total) * 100) : 0; if (!m.current) m.current = "Terminé"; });
    setStagesByProject(map);
  };
  const loadLeads = async () => {
    const { data } = await supabase.from("leads").select("*").order("created_at", { ascending: false });
    setLeads(data || []);
  };
  const updateLead = async (id, patch) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    await supabase.from("leads").update(patch).eq("id", id);
  };
  useEffect(() => { loadProjects(); loadLeads(); }, []);

  const createProject = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return; // email désormais optionnel
    // Contacts valides (email renseigné), dédoublonnés
    const contacts = [];
    const seen = new Set();
    (form.clients || []).forEach((c) => {
      const mail = (c.email || "").trim().toLowerCase();
      const tel = (c.phone || "").trim();
      if (!mail && !tel) return; // ni email ni téléphone → on ignore
      const key = mail || `tel:${tel}`;
      if (seen.has(key)) return;
      seen.add(key);
      contacts.push({ email: mail || null, phone: tel || null });
    });
    setCreating(true);
    const { data, error } = await supabase.from("projects").insert({
      name: form.name.trim(),
      client_email: contacts.find((c) => c.email)?.email || null, // contact principal (facultatif)
      secteur: form.secteur || null,
    }).select().single();
    if (!error && data && contacts.length) {
      await supabase.from("project_clients").insert(contacts.map((c) => ({ project_id: data.id, ...c })));
    }
    setCreating(false);
    if (data) {
      setForm({ name: "", secteur: "", clients: [emptyContact()] });
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      await loadProjects();
      setSelected(data);
    }
  };

  // ---- Detail view ----
  if (selected) {
    return (
      <div className="w-full">
        <button onClick={() => { setSelected(null); loadProjects(); loadLeads(); }}
          className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Tous les projets
        </button>
        <ClientAccess project={selected} />
        <ProjectPanel project={selected} profile={profile} isAdmin={true} />
      </div>
    );
  }

  const byStatus = (s) => projects.filter((p) => p.status === s).length;
  const enCours = projects.filter((p) => EN_COURS.includes(p.status)).length;
  const termines = byStatus("termine");
  const leadsAudit = leads.filter((l) => l.source === "audit").length;
  const leadsMockup = leads.filter((l) => l.source === "mockup").length;
  const pmeta = (id) => stagesByProject[id] || { pct: 0, current: "—" };
  const devisBadge = (p) => {
    if (!p.devis_status || p.devis_status === "aucun") return null;
    const d = DEVIS_STATUS.find((x) => x.id === p.devis_status);
    const paid = p.devis_status === "acompte_paye" || p.devis_status === "solde_paye";
    const due = p.devis_status === "acompte_a_payer" || p.devis_status === "solde_a_payer";
    const cls = paid ? "border-emerald-400/40 text-emerald-400" : due ? "border-amber-400/40 text-amber-400" : "border-white/10 text-zinc-400";
    return <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${cls}`}>{d?.label}</span>;
  };

  const TABS = [
    { id: "apercu", label: "Vue d'ensemble", icon: LayoutGrid },
    { id: "projets", label: "Projets", icon: FolderKanban },
    { id: "leads", label: `Leads (${leads.length})`, icon: Users },
  ];

  return (
    <div>
      <div className="flex gap-2 mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${tab === t.id ? "bg-[var(--accent)] text-black" : "border border-white/[0.1] text-zinc-400 hover:text-white"}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* VUE D'ENSEMBLE */}
      {tab === "apercu" && (
        <div className="space-y-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { k: projects.length, v: "Projets au total" },
              { k: enCours, v: "En cours" },
              { k: termines, v: "Terminés" },
              { k: leads.length, v: "Leads captés" },
            ].map((s) => (
              <div key={s.v} className="bento p-6">
                <div className="font-display text-4xl tracking-tighter text-[var(--accent)]">{s.k}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.v}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bento p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Projets récents</h3>
                <button onClick={() => setTab("projets")} className="text-[11px] text-[var(--accent)] hover:underline">Tout voir →</button>
              </div>
              <div className="space-y-2.5">
                {projects.slice(0, 5).map((p) => (
                  <button key={p.id} onClick={() => setSelected(p)} className="w-full text-left rounded-xl border border-white/[0.06] px-4 py-3 hover:border-[var(--accent)]/40 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm text-zinc-200 truncate">{p.name}</span>
                      <span className="shrink-0 rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-[var(--accent)]">{PROJECT_STATUS.find((s) => s.id === p.status)?.label}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="h-1 flex-1 rounded-full bg-white/[0.08] overflow-hidden"><div className="h-full bg-[var(--accent)]" style={{ width: `${pmeta(p.id).pct}%` }} /></div>
                      <span className="text-[10px] text-zinc-500 shrink-0">{pmeta(p.id).pct}%</span>
                      {devisBadge(p)}
                    </div>
                  </button>
                ))}
                {projects.length === 0 && <p className="text-sm text-zinc-500">Aucun projet. Créez le premier dans l'onglet Projets.</p>}
              </div>
            </div>

            <div className="bento p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Derniers leads</h3>
                <span className="text-[11px] text-zinc-500">{leadsAudit} audit · {leadsMockup} mockup</span>
              </div>
              <div className="space-y-2">
                {leads.slice(0, 5).map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{l.email || l.commerce || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{l.source}{l.score != null ? ` · ${l.score}/18` : ""}</div>
                    </div>
                    {l.email && <a href={`mailto:${l.email}`} className="text-[var(--accent)] shrink-0"><Mail className="w-4 h-4" /></a>}
                  </div>
                ))}
                {leads.length === 0 && <p className="text-sm text-zinc-500">Aucun lead pour l'instant.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PROJETS */}
      {tab === "projets" && (
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="bento p-6">
              <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Nouveau projet</h3>
              <form onSubmit={createProject} className="space-y-4">
                <input className="field" placeholder="Nom du projet / commerce" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />

                <div className="space-y-3">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">Accès clients (optionnel)</div>
                  {form.clients.map((c, i) => (
                    <div key={i} className="rounded-xl border border-white/[0.06] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Contact {i + 1}</span>
                        {form.clients.length > 1 && (
                          <button type="button" onClick={() => setForm((f) => ({ ...f, clients: f.clients.filter((_, j) => j !== i) }))}
                            className="text-zinc-500 hover:text-red-400" title="Retirer"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                      <input className="field w-full" type="email" placeholder="Email (pour l'accès à l'espace)" value={c.email}
                        onChange={(e) => setForm((f) => ({ ...f, clients: f.clients.map((x, j) => j === i ? { ...x, email: e.target.value } : x) }))} />
                      <input className="field w-full" type="tel" placeholder="Téléphone (optionnel)" value={c.phone}
                        onChange={(e) => setForm((f) => ({ ...f, clients: f.clients.map((x, j) => j === i ? { ...x, phone: e.target.value } : x) }))} />
                    </div>
                  ))}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, clients: [...f.clients, emptyContact()] }))}
                    className="text-[11px] text-[var(--accent)] hover:underline inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Ajouter un contact
                  </button>
                </div>

                <select className="field bg-transparent" value={form.secteur} onChange={(e) => setForm((f) => ({ ...f, secteur: e.target.value }))}>
                  <option value="" className="bg-black">— Secteur (optionnel) —</option>
                  {secteurOptions.map((s) => <option key={s.id} value={s.label} className="bg-black">{s.label}</option>)}
                </select>
                <button className="btn-pill btn-pill-accent w-full justify-center" disabled={creating}>{creating ? "Création…" : "Créer le projet"} <Plus className="w-4 h-4" /></button>
                <p className="text-[11px] text-zinc-500 leading-relaxed">Chaque contact peut être un email, un téléphone, ou les deux. L'<strong>email</strong> sert à se connecter à l'espace (lien magique) ; le téléphone est un simple contact. Un même email peut être relié à plusieurs projets. Sans contact, le projet reste privé (vous seul le voyez). Vous pourrez tout modifier ensuite.</p>
              </form>
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-4">{projects.length} projet{projects.length > 1 ? "s" : ""}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {projects.map((p) => (
                <button key={p.id} onClick={() => setSelected(p)} className="text-left bento p-5 hover:border-[var(--accent)]/40 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg tracking-tight truncate">{p.name}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">{p.client_email}</div>
                    </div>
                    <span className="shrink-0 rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-[var(--accent)]">{PROJECT_STATUS.find((s) => s.id === p.status)?.label}</span>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1.5"><span className="truncate">Étape : {pmeta(p.id).current}</span><span>{pmeta(p.id).pct}%</span></div>
                    <div className="h-1 w-full rounded-full bg-white/[0.08] overflow-hidden"><div className="h-full bg-[var(--accent)]" style={{ width: `${pmeta(p.id).pct}%` }} /></div>
                  </div>
                  {devisBadge(p) && <div className="mt-3">{devisBadge(p)}</div>}
                </button>
              ))}
              {projects.length === 0 && <p className="text-sm text-zinc-500">Aucun projet. Créez le premier.</p>}
            </div>
          </div>
        </div>
      )}

      {/* LEADS — mini-CRM */}
      {tab === "leads" && (
        <div className="space-y-6">
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {LEAD_STATUS.map((s) => (
              <div key={s.id} className="bento p-4 text-center">
                <div className="font-display text-2xl">{leads.filter((l) => (l.status || "nouveau") === s.id).length}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="bento p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/[0.08]">
                  <th className="py-3 pr-4">Date</th><th className="py-3 pr-4">Source</th><th className="py-3 pr-4">Contact</th>
                  <th className="py-3 pr-4">Secteur</th><th className="py-3 pr-4">Score</th><th className="py-3 pr-4">Statut</th><th className="py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/[0.04] text-zinc-300 align-top">
                    <td className="py-3 pr-4 text-zinc-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="py-3 pr-4"><span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase">{l.source}</span></td>
                    <td className="py-3 pr-4">
                      {l.email ? <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-[var(--accent)]"><Mail className="w-3 h-3" />{l.email}</a> : "—"}
                      {l.commerce && <div className="text-[11px] text-zinc-500">{l.commerce}</div>}
                    </td>
                    <td className="py-3 pr-4">{l.secteur || "—"}</td>
                    <td className="py-3 pr-4">{l.score != null ? `${l.score}/18` : "—"}</td>
                    <td className="py-3 pr-4">
                      <select value={l.status || "nouveau"} onChange={(e) => updateLead(l.id, { status: e.target.value })}
                        className="bg-transparent border border-white/10 rounded-lg px-2 py-1 text-xs text-zinc-200 outline-none">
                        {LEAD_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-black">{s.label}</option>)}
                      </select>
                    </td>
                    <td className="py-3">
                      <input defaultValue={l.notes || ""} onBlur={(e) => updateLead(l.id, { notes: e.target.value })} placeholder="Note…"
                        className="bg-transparent border border-white/10 rounded-lg px-2 py-1 text-xs text-zinc-200 outline-none w-40" />
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-zinc-500">Aucun lead pour l'instant.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
